import { v4 as uuidv4 } from 'uuid';
import { formatUTCDate } from '@/src/utils/formatUtils';
import {
  EmployeePCReimbursement,
  PCReimbursementArgs,
  PCReimbursementDetailWithId,
  PCReimbursementDetails,
  PCReimbursementSummary,
} from '../../../../Interfaces/Accounting/Payroll.interface';

const setEmpDetails = (emp: EmployeePCReimbursement) => {
  let hasDetails = false;
  emp.details?.forEach((detail) => {
    const keysLength = Object.keys(detail).length - 1;

    hasDetails = hasDetails || !!keysLength;
  });

  emp.useDetails = hasDetails;
};

const isEmployeeDetailActive = (detail: PCReimbursementDetails) => {
  return !!(detail.amount || detail.job || detail.overHead);
};

export const getEmployeeActiveDetailsNumber = (
  emp: EmployeePCReimbursement
) => {
  let counter = 0;
  const details = emp.details || [];

  details.forEach((detail) => {
    if (isEmployeeDetailActive(detail)) {
      counter += 1;
    }
  });

  return counter;
};

export const findEmployee = (
  employees: EmployeePCReimbursement[],
  empID: number
) => {
  let empIndex = 0;
  const selectedEmp = employees.find((emp, index) => {
    if (emp.empID === empID) {
      empIndex = index;
      return true;
    }

    return false;
  });

  if (!selectedEmp) {
    return;
  }

  return { index: empIndex, employee: selectedEmp };
};

export const updateDetails = (
  employee: EmployeePCReimbursement,
  detailIndex: number,
  detail: PCReimbursementDetailWithId
) => {
  if (!employee.details) {
    return;
  }

  const empCopy = { ...employee };
  const { newDetails, selectedDetail } = empCopy.details!.reduce(
    (acc, prevDetail, index) => {
      if (index === detailIndex) {
        acc.selectedDetail = prevDetail;
        acc.newDetails.push(detail);
      } else {
        acc.newDetails.push(prevDetail);
      }

      return acc;
    },
    {
      newDetails: [] as PCReimbursementDetailWithId[],
      selectedDetail: null as PCReimbursementDetailWithId | null,
    }
  );

  if (!selectedDetail) {
    return;
  }

  empCopy.details = newDetails;

  const prevUseDetails = empCopy.useDetails;
  setEmpDetails(empCopy);
  if (!prevUseDetails && empCopy.useDetails) {
    empCopy.total = null;
    delete empCopy.amount;
    delete empCopy.job;
    delete empCopy.phaseCode;
    delete empCopy.overHead;
  }

  if (selectedDetail.amount !== detail.amount) {
    empCopy.total =
      (empCopy.total ?? 0) -
      (selectedDetail.amount ?? 0) +
      (detail.amount ?? 0);

    if (!empCopy.total) {
      empCopy.total = null;
    } else {
      empCopy.total = Math.round(empCopy.total * 100) / 100;
    }
  }

  return empCopy;
};

export const addNewDetail = (employee: EmployeePCReimbursement) => {
  const empCopy = { ...employee };
  empCopy.details?.push({ id: uuidv4() });
  empCopy.details = [...(empCopy.details ?? [])];

  return empCopy;
};

export const removeDetail = (
  employee: EmployeePCReimbursement,
  detailIndex: number
) => {
  if (!employee.details) {
    return;
  }

  const empCopy = { ...employee };
  const { remainingDetails, removedDetail } = empCopy.details!.reduce(
    (acc, detail, index) => {
      if (detailIndex === index) {
        acc.removedDetail = detail;
      } else {
        acc.remainingDetails.push(detail);
      }

      return acc;
    },
    {
      remainingDetails: [] as PCReimbursementDetailWithId[],
      removedDetail: null as PCReimbursementDetailWithId | null,
    }
  );

  if (!removedDetail) {
    return;
  }

  empCopy.total = (empCopy.total ?? 0) - (removedDetail.amount ?? 0);

  if (!empCopy.total) {
    empCopy.total = null;
  } else {
    empCopy.total = Math.round(empCopy.total * 100) / 100;
  }

  empCopy.details = remainingDetails;
  setEmpDetails(empCopy);

  return empCopy;
};

export const updateAttachments = (
  employees: EmployeePCReimbursement[],
  prevEmployees: EmployeePCReimbursement[]
) => {
  prevEmployees.forEach((emp) => {
    const newEmp = employees.find((newEmp) => newEmp.empID === emp.empID);
    emp.attachments = newEmp?.attachments;

    if (emp.newAttachments) {
      delete emp.newAttachments;
    }

    if (emp.deleteAttachments) {
      delete emp.deleteAttachments;
    }
  });

  return [...prevEmployees];
};

export const buildFormDataPayload = (
  data: PCReimbursementArgs,
  extraKeys: Record<string, string>
) => {
  const formData = new FormData();

  Object.entries(extraKeys).forEach((value) => {
    const [key, content] = value;

    formData.append(key, content);
  });

  if (!data.length) {
    formData.append('employees', JSON.stringify([]));
    return formData;
  }

  data.forEach((employee, index) => {
    formData.append(
      `employees[${index}]`,
      JSON.stringify({
        empID: employee.empID,
        total: employee.total,
        details: employee.details,
        amount: employee.amount,
        job: employee.job,
        phaseCode: employee.phaseCode,
        overHead: employee.overHead,
        deleteAttachments: employee.deleteAttachments,
      })
    );

    if (employee.attachments) {
      employee.attachments.forEach((attachment, attachmentIndex) => {
        formData.append(
          `employees[${index}][attachments][${attachmentIndex}]`,
          attachment
        );
      });
    }
  });

  return formData;
};

export const formatReimbursementEmployees = (
  data: EmployeePCReimbursement[]
) => {
  const newData = data.map((emp) => {
    const empCoy = { ...emp };

    if (empCoy.details) {
      empCoy.useDetails = true;
    } else {
      empCoy.details = [{ id: uuidv4() }];
    }

    return empCoy;
  });

  newData.push({ empID: null, total: null, details: [{ id: uuidv4() }] });
  return newData;
};

export const checkReimbursementContent = (draft: EmployeePCReimbursement[]) => {
  let complete = true;
  const hasEmptyValue = (detail: PCReimbursementDetails) => {
    return (
      (!detail.job && !detail.overHead) || (detail.job && !detail.phaseCode)
    );
  };

  draft.forEach((emp) => {
    if (!emp.empID) {
      return;
    }

    if (emp.useDetails) {
      const { details } = emp;

      details?.forEach((detail) => {
        if (hasEmptyValue(detail)) {
          complete = false;
        }
      });
    } else {
      if (hasEmptyValue(emp)) {
        complete = false;
      }
    }
  });

  return complete;
};

export const prepareReimbursementPayload = (
  draft: EmployeePCReimbursement[]
) => {
  const processedDraft = draft.reduce((acc, emp) => {
    if (!emp.empID) {
      return acc;
    }

    const empCopy = {
      ...emp,
      empID: emp.empID,
      total: emp.total || 0,
      attachments: emp.newAttachments,
    };
    if (!empCopy.useDetails) {
      delete empCopy.details;
    } else {
      empCopy.details = empCopy.details?.reduce((acc, detail) => {
        if (Object.keys(detail).length - 1 > 0) {
          acc.push(detail);
        }

        return acc;
      }, [] as PCReimbursementDetailWithId[]);
    }

    delete empCopy.useDetails;
    delete empCopy.firstName;
    delete empCopy.lastName;
    delete empCopy.newAttachments;

    acc.push(empCopy);

    return acc;
  }, [] as PCReimbursementArgs);

  return processedDraft;
};

export const getSubmittedDraft = (
  seledtedDraft: string,
  data: PCReimbursementSummary[]
) => {
  const draft = data?.find((draft) => draft.id === seledtedDraft);

  return `${formatUTCDate(draft?.updatedAt ?? '')} - ${draft?.userName}`;
};

export const moveEmployeeValuesToDetail = (
  employee: EmployeePCReimbursement
) => {
  if (
    !employee.useDetails &&
    (employee.amount || employee.job || employee.overHead)
  ) {
    const employeeDetails = employee.details ?? [];
    const firstDetail = employeeDetails[0] ?? {};
    firstDetail.job = employee.job;
    firstDetail.phaseCode = employee.phaseCode;
    firstDetail.overHead = employee.overHead;
    firstDetail.amount = employee.amount;

    delete employee.job;
    delete employee.phaseCode;
    delete employee.amount;
    delete employee.overHead;
    employee.useDetails = true;
  }
};

export const moveDetailValuesToEmployee = (
  employee: EmployeePCReimbursement
) => {
  const employeeDetails = employee.details ?? [];
  const firstDetail = employeeDetails[0] ?? {};

  if (
    employee.useDetails &&
    getEmployeeActiveDetailsNumber(employee) === 1 &&
    isEmployeeDetailActive(firstDetail)
  ) {
    employee.job = firstDetail.job;
    employee.phaseCode = firstDetail.phaseCode;
    employee.overHead = firstDetail.overHead;
    employee.amount = firstDetail.amount;
    delete firstDetail.job;
    delete firstDetail.phaseCode;
    delete firstDetail.overHead;
    delete firstDetail.amount;
    employee.useDetails = false;
  }
};
